import React, { useEffect, useState, useCallback } from 'react';
import { Mail, Sparkles, BellRing, CheckCircle2 } from 'lucide-react';
import { apiFetch } from '@/hooks/useFetch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

/**
 * Newsletter Topic Preferences card.
 *
 * Lets a logged-in user pick which newsletter streams they want:
 *   • weekly_phrase — Wednesday "Phrase of the Week" digest
 *   • engagement   — Friday engagement / tips email
 *
 * If the user's email isn't yet in `newsletter_subscribers`, we offer a
 * one-click subscribe so they don't have to copy-paste their email
 * elsewhere.
 */
export const NewsletterPreferencesCard = ({ email, isSpanish = false }) => {
  const [topics, setTopics] = useState({ weekly_phrase: true, engagement: true });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isSubscriber, setIsSubscriber] = useState(false);
  const [unsubscribed, setUnsubscribed] = useState(false);

  const loadPrefs = useCallback(async () => {
    if (!email) return;
    setLoading(true);
    const { data, error, status } = await apiFetch(
      `/api/newsletter/preferences?email=${encodeURIComponent(email)}`
    );
    if (data && !error) {
      setIsSubscriber(true);
      setUnsubscribed(Boolean(data.unsubscribed_at));
      const arr = Array.isArray(data.topics) ? data.topics : [];
      setTopics({
        weekly_phrase: arr.includes('weekly_phrase'),
        engagement: arr.includes('engagement'),
      });
    } else if (status === 404) {
      setIsSubscriber(false);
    }
    setLoading(false);
  }, [email]);

  useEffect(() => { loadPrefs(); }, [loadPrefs]);

  const persist = async (next) => {
    if (!email || !isSubscriber) return;
    setSaving(true);
    const list = Object.entries(next).filter(([, v]) => v).map(([k]) => k);
    const { error } = await apiFetch('/api/newsletter/preferences', {
      method: 'POST',
      body: { email, topics: list },
    });
    setSaving(false);
    if (error) {
      toast.error(isSpanish ? 'No se pudo guardar' : 'Could not save preferences');
      // revert on failure
      loadPrefs();
    } else {
      toast.success(isSpanish ? 'Preferencias guardadas' : 'Preferences saved');
    }
  };

  const handleToggle = (key) => {
    const next = { ...topics, [key]: !topics[key] };
    setTopics(next);
    persist(next);
  };

  const handleSubscribe = async () => {
    if (!email) return;
    setSaving(true);
    const { error } = await apiFetch('/api/newsletter/subscribe', {
      method: 'POST',
      body: {
        email,
        source: 'account_settings',
        topics: ['weekly_phrase', 'engagement'],
      },
    });
    setSaving(false);
    if (error) {
      toast.error(isSpanish ? 'No se pudo suscribir' : 'Could not subscribe');
    } else {
      toast.success(isSpanish ? '¡Suscrito!' : 'Subscribed!');
      loadPrefs();
    }
  };

  if (!email) return null;

  return (
    <Card className="mb-6" data-testid="newsletter-preferences-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="h-5 w-5" />
          {isSpanish ? 'Preferencias del boletín' : 'Newsletter Preferences'}
        </CardTitle>
        <CardDescription>
          {isSpanish
            ? 'Elige qué correos del boletín quieres recibir.'
            : 'Choose which newsletter emails you want to receive.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <p className="text-sm text-muted-foreground">{isSpanish ? 'Cargando…' : 'Loading…'}</p>
        ) : !isSubscriber ? (
          <div className="rounded-lg border border-dashed p-4 text-sm">
            <p className="mb-3">
              {isSpanish
                ? 'Aún no estás suscrito a nuestro boletín.'
                : 'You\'re not subscribed to our newsletter yet.'}
            </p>
            <Button
              type="button"
              onClick={handleSubscribe}
              disabled={saving}
              data-testid="newsletter-subscribe-from-settings-btn"
              className="bg-[#00E5CC] text-[#0d1f2d] hover:bg-[#00E5CC]/90"
            >
              {isSpanish ? 'Suscribirme con este correo' : 'Subscribe with this email'}
            </Button>
          </div>
        ) : unsubscribed ? (
          <div className="rounded-lg border border-dashed p-4 text-sm">
            <p className="mb-3">
              {isSpanish
                ? 'Te diste de baja del boletín. ¿Quieres volver a suscribirte?'
                : 'You unsubscribed from the newsletter. Want to re-subscribe?'}
            </p>
            <Button
              type="button"
              onClick={handleSubscribe}
              disabled={saving}
              data-testid="newsletter-resubscribe-btn"
              className="bg-[#00E5CC] text-[#0d1f2d] hover:bg-[#00E5CC]/90"
            >
              {isSpanish ? 'Volver a suscribirme' : 'Re-subscribe'}
            </Button>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between gap-4">
              <Label htmlFor="topic-weekly-phrase" className="flex-1 cursor-pointer">
                <span className="flex items-center gap-2 font-medium">
                  <Sparkles className="h-4 w-4 text-[#00E5CC]" />
                  {isSpanish ? 'Frase de la semana' : 'Weekly Phrase of the Week'}
                </span>
                <p className="text-sm text-muted-foreground mt-1">
                  {isSpanish
                    ? 'Una frase nueva cada miércoles, 9 a.m. CT.'
                    : 'One new phrase every Wednesday, 9 AM CT.'}
                </p>
              </Label>
              <Switch
                id="topic-weekly-phrase"
                checked={topics.weekly_phrase}
                onCheckedChange={() => handleToggle('weekly_phrase')}
                disabled={saving}
                data-testid="newsletter-topic-weekly-phrase-toggle"
              />
            </div>
            <div className="flex items-start justify-between gap-4 pt-3 border-t">
              <Label htmlFor="topic-engagement" className="flex-1 cursor-pointer">
                <span className="flex items-center gap-2 font-medium">
                  <BellRing className="h-4 w-4 text-[#00E5CC]" />
                  {isSpanish ? 'Consejos de comunicación' : 'Weekly Engagement Tips'}
                </span>
                <p className="text-sm text-muted-foreground mt-1">
                  {isSpanish
                    ? 'Estrategias y recordatorios prácticos cada viernes.'
                    : 'Practical strategies & reminders every Friday.'}
                </p>
              </Label>
              <Switch
                id="topic-engagement"
                checked={topics.engagement}
                onCheckedChange={() => handleToggle('engagement')}
                disabled={saving}
                data-testid="newsletter-topic-engagement-toggle"
              />
            </div>
            {!topics.weekly_phrase && !topics.engagement && (
              <p className="text-xs text-amber-600 dark:text-amber-400 pt-1 flex items-center gap-1">
                {isSpanish
                  ? 'Has desactivado todos los correos del boletín.'
                  : 'You\'ve turned off all newsletter emails.'}
              </p>
            )}
            {(topics.weekly_phrase || topics.engagement) && (
              <p className="text-xs text-muted-foreground pt-1 flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                {isSpanish ? 'Cambios guardados automáticamente.' : 'Changes saved automatically.'}
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default NewsletterPreferencesCard;
